import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { publicPortalApi } from '@/lib/api/portal.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';
import { PortalProgress } from '@/components/portal/PortalProgress.js';
import { PublicShell } from './PortalTokenViewPage.js';
import { parseApiError } from '@/lib/api/errors.js';

export function PublicTrackPage() {
  const [params] = useSearchParams();
  // Optional embed filter only — never shown as a required field
  const embedSlug = (params.get('store') ?? params.get('tenant') ?? '').trim() || undefined;

  const [phone, setPhone] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  const [results, setResults] = useState<Array<Record<string, unknown>> | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      publicPortalApi.track({
        phone,
        ticketNumber: ticketNumber || undefined,
        tenantSlug: embedSlug,
      }),
    onSuccess: (data) => {
      setError(null);
      if (!data.found || data.tickets.length === 0) {
        setNotFound(true);
        setResults([]);
      } else {
        setNotFound(false);
        setResults(data.tickets);
      }
    },
    onError: (err) => {
      setNotFound(false);
      setResults([]);
      setError(parseApiError(err).message);
    },
  });

  return (
    <PublicShell>
      <h1 className="text-center text-xl font-bold">Check Your Repair Status</h1>
      <form
        className="mt-6 space-y-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <label className="block text-sm font-medium">
          Phone Number
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 000-1111"
            required
          />
        </label>
        <label className="block text-sm font-medium">
          Ticket Number <span className="font-normal text-slate-400">(optional)</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={ticketNumber}
            onChange={(e) => setTicketNumber(e.target.value)}
            placeholder="TKT-_____"
          />
        </label>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        <button
          type="submit"
          disabled={mutation.isPending}
          className="w-full rounded-xl bg-[#1B4F8A] px-4 py-3 text-sm font-semibold text-white"
        >
          Check Status →
        </button>
      </form>

      {notFound ? (
        <p className="mt-4 text-center text-sm text-slate-500">No repairs found for that number</p>
      ) : null}

      <div className="mt-4 space-y-3">
        {(results ?? []).map((t) => (
          <div
            key={String(t.ticketNumber)}
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="font-bold">{String(t.ticketNumber)}</div>
            <p className="text-sm text-slate-600">{String(t.deviceSummary ?? '')}</p>
            {(t.storeInfo as { name?: string } | null)?.name ? (
              <p className="text-xs text-slate-400">{(t.storeInfo as { name: string }).name}</p>
            ) : null}
            <p className="mt-2 text-sm font-semibold text-blue-800">{String(t.statusLabel ?? '')}</p>
            <p className="text-sm text-slate-500">{String(t.statusDescription ?? '')}</p>
            <div className="mt-3">
              <PortalProgress index={Number(t.progressIndex ?? 0)} />
            </div>
            {t.promisedDate ? (
              <p className="mt-2 text-xs text-slate-500">
                Est. ready: {formatDateTime(String(t.promisedDate))}
              </p>
            ) : null}
            {Number(t.balanceDue) > 0 ? (
              <p className="text-sm font-semibold">Due: {formatCurrency(String(t.balanceDue))}</p>
            ) : null}
          </div>
        ))}
      </div>
    </PublicShell>
  );
}
